import { WebViewEvents } from '@consts';
import { useEffect, useState } from 'react';

/**
 * Hook that detects if the application is running inside a Flutter WebView,
 * provides functionality for WebView interactions, and handles Auth0 authentication
 * status in WebView environments
 */
export const useWebView = () => {
  // WebView detection
  const [isWebView, setIsWebView] = useState(false);
  const [isDetectingWebView, setIsDetectingWebView] = useState(true);

  // Auth0 status in WebView
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Check if running in WebView
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // For communication with native app in flutter webview we create dedicated channel
      // This channel has dual purpose:
      // 1. To check if we are in a flutter webview
      // 2. To send messages to the native app
      if (window.Flutter) {
        setIsWebView(true);
      } else {
        setIsWebView(false);
      }
      setIsDetectingWebView(false);
    }
  }, []);

  // Initialize Auth0 keys check
  const initAuth0KeysCheck = () => {
    if (typeof window === 'undefined' || !window.localStorage) return false;

    // Check for keys starting with @@auth0spajs@@
    let numberOfAuth0Keys = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('@@auth0spajs@@')) {
        numberOfAuth0Keys++;
      }
    }

    // Auth0 use 2 keys to store tokens
    return numberOfAuth0Keys >= 2;
  };

  // Check Auth0 keys status (separate from initialization)
  const checkAuth0Keys = () => {
    const hasKeys = initAuth0KeysCheck();

    if (hasKeys) {
      setIsAuthReady(true);
    } else if (retryCount < 10) {
      setTimeout(() => {
        setRetryCount((prev) => prev + 1);
      }, 1000);
    } else {
      // Notify mobile app about logout if available
      postMessageToWebView(WebViewEvents.LOGOUT);
      throw new Error('hasAuth0Keys false'); // let notify sentry
    }
  };

  // Check for Auth0 keys in localStorage when in WebView
  useEffect(() => {
    if (isDetectingWebView) {
      return;
    }

    // if we are not in webview, we don't need to check for auth0 keys
    if (!isWebView) {
      setIsAuthReady(true);
    } else {
      // if we are in webview we need to check for auth0 keys
      checkAuth0Keys();
    }
  }, [isWebView, retryCount, isDetectingWebView]);

  /**
   * Send a message to the Flutter WebView if available
   */
  const postMessageToWebView = (message: string) => {
    if (isWebView && typeof window !== 'undefined' && window.Flutter) {
      window.Flutter.postMessage(message);
      return true;
    }
    return false;
  };

  return {
    // WebView state
    isWebView,
    isDetectingWebView,
    postMessageToWebView,

    // Auth0 state
    isAuthReady
  };
};
